import { useEffect, useMemo, useRef } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-markercluster'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
// @ts-expect-error no type declarations for CSS import
import 'react-leaflet-markercluster/styles'
import useStore from '../store/useStore'
import { useSites } from '../hooks/useQueries'
import { ConflictOverlay } from './TimelineScrubber'
import StartHereIndicator from './StartHereIndicator'
import type { SiteFeature } from '../types'

// Fix default marker icon paths for bundled leaflet
import iconUrl from 'leaflet/dist/images/marker-icon.png'
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import shadowUrl from 'leaflet/dist/images/marker-shadow.png'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl })

const DEFAULT_CENTER: L.LatLngExpression = [30, 20]
const DEFAULT_ZOOM = 3

function escHtml(str: string | undefined): string {
  if (!str) return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Inner component that reacts to filter-driven bound changes */
function FitBounds({
  features,
  country,
  isLoading,
}: {
  features: SiteFeature[]
  country: string
  isLoading: boolean
}) {
  const map = useMap()
  const prevCountry = useRef(country)
  const pendingFly = useRef(false)

  // When the country filter changes, mark that we need to fly once new data arrives
  useEffect(() => {
    if (country !== prevCountry.current) {
      pendingFly.current = true
      prevCountry.current = country

      // "All Countries" selected — fly back to default immediately
      if (!country) {
        pendingFly.current = false
        map.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM)
      }
    }
  }, [country, map])

  // When features finish loading and a fly is pending, execute it
  useEffect(() => {
    if (!pendingFly.current || isLoading) return
    pendingFly.current = false

    if (features.length > 0) {
      const bounds = L.latLngBounds(
        features.map((f) => [f.geometry.coordinates[1], f.geometry.coordinates[0]]),
      )
      if (bounds.isValid()) {
        map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 12, animate: true })
      }
    }
  }, [features, isLoading, map])

  return null
}

interface SiteMarkersProps {
  features: SiteFeature[]
  onSiteCountChange: (count: number) => void
}

function SiteMarkers({ features, onSiteCountChange }: SiteMarkersProps) {
  const searchQuery = useStore((s) => s.searchQuery)
  const openSite = useStore((s) => s.openSite)
  const map = useMap()

  const filtered = useMemo(() => {
    if (!searchQuery) return features
    return features.filter((f) => f.properties.name.toLowerCase().includes(searchQuery))
  }, [features, searchQuery])

  useEffect(() => {
    onSiteCountChange(filtered.length)
  }, [filtered.length, onSiteCountChange])

  const handleViewDetails = (id: string) => {
    map.closePopup()
    openSite(id)
  }

  return (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={50}
      spiderfyOnMaxZoom
      showCoverageOnHover={false}
      disableClusteringAtZoom={14}
    >
      {filtered.map((f) => {
        const [lon, lat] = f.geometry.coordinates
        const p = f.properties
        const opacity = p.confidenceScore != null && p.confidenceScore < 0.5 ? 0.4 : 1.0

        return (
          <Marker key={f.id} position={[lat, lon]} opacity={opacity}>
            <Popup>
              <div className="site-popup">
                <h3>{escHtml(p.name)}</h3>
                <div className="popup-meta">
                  {[p.country, p.period].filter(Boolean).join(' \u00b7 ')}
                </div>
                <button onClick={() => handleViewDetails(f.id)}>View Details</button>
              </div>
            </Popup>
          </Marker>
        )
      })}
    </MarkerClusterGroup>
  )
}

interface SiteMapProps {
  onSiteCountChange: (count: number) => void
}

export default function SiteMap({ onSiteCountChange }: SiteMapProps) {
  const countryFilter = useStore((s) => s.countryFilter)
  const filters = useMemo(() => (countryFilter ? { country: countryFilter } : {}), [countryFilter])
  const { data, isLoading } = useSites(filters)
  const features = data?.features ?? []

  return (
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      style={{ width: '100%', height: '100%' }}
      id="map"
      aria-label="Interactive map of archaeological sites"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={18}
      />
      <FitBounds features={features} country={countryFilter} isLoading={isLoading} />
      <SiteMarkers features={features} onSiteCountChange={onSiteCountChange} />
      <ConflictOverlay />
      <StartHereIndicator features={features} />
    </MapContainer>
  )
}
